import { useCallback, useEffect, useMemo } from 'react';

import { useNavigate, useParams } from '@tanstack/react-router';

import { useTicketDetailsFormDefinition } from '@/_containers/support/shared/form-context';
import { resetFormState, useEdit, useInvalidate, usePostAuditLog, useProfileCrudActions } from '@/_shared';

import { useRevisionHistory } from './use-revision-history';

import { useDeleteMutation, useGetCompany, useUpdateMutation } from '../apis';
import { clientFormToUpdate, clientQueryToForm } from '../converters';
import { useFormDefinition } from '../form-context';
import type { CompanyFormSchema, CompanyQuerySchema, CompanyUpdateSchema } from '../schemas';

export function usePage() {
  const nav = useNavigate();
  const edit = useEdit();
  const form = useFormDefinition();
  const { id } = useParams({ strict: false });
  usePostAuditLog({ endpoint: 'clients', id: `${id}` });
  const { setHasToInvalidate, removeQuery } = useInvalidate(['COMPANY', id ?? '']);
  const ticketDetailsForm = useTicketDetailsFormDefinition();
  const client = useGetCompany(`${id}`);
  const updateMutation = useUpdateMutation(`${id}`);
  const deleteMutation = useDeleteMutation();

  const hasActiveAgreement = useMemo(() => {
    const clientData = client?.data.data;
    if (!clientData) {
      return false;
    }

    // const agreementActiveStatus: AgreementStatusQuerySchema[] = ['Execution'];

    return clientData.agreements.some((agreement) => !!agreement.execution_date);
  }, [client.data]);

  const handleSuccessSideEffects = useCallback(() => {
    removeQuery(['COMPANIES']);
    setHasToInvalidate.setTrue();
  }, [removeQuery, setHasToInvalidate]);

  const { onUpdate, onPartialUpdate, onDelete, isDeleting, isPending, isSuccess } = useProfileCrudActions<
    CompanyFormSchema,
    CompanyUpdateSchema,
    { data: CompanyQuerySchema }
  >({
    form,
    edit,
    formToMutation: clientFormToUpdate,
    updateMutation,
    deleteMutation,
    deleteVariables: { id: `${id}` },
    updateSuccessMessage: 'Company has been updated.',
    deleteSuccessMessage: 'Company has been deleted.',
    onSuccessSideEffects: handleSuccessSideEffects,
    onDeleteSuccess: () => {
      nav({ to: '/companies' });
    },
  });

  const onSubmit = useCallback(
    async (data: CompanyFormSchema) => {
      onUpdate(clientFormToUpdate({ ...data }));
    },
    [onUpdate]
  );

  const revisionHistory = useRevisionHistory(form, onUpdate);

  useEffect(() => {
    const { data: response } = client;
    if (response) {
      resetFormState(form, clientQueryToForm(response.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.data]);

  console.log(form.formState.errors);

  return {
    id,
    form,
    edit,
    isPending,
    isSuccess,
    isDeleting,
    hasActiveAgreement,
    onSubmit,
    onDelete,
    onPartialUpdate,
    revisionHistory,
    isReverting: client.isFetching || isPending,
    ticketDetailsForm,
  };
}

export type UsePageType = ReturnType<typeof usePage>;
